/* assets/size-gate.js (SAFE VERSION)
   - No MutationObserver (prevents feedback loops/stalls)
   - Gates add-to-cart AND dynamic checkout until Size is selected
   - Fixes Quick Add: selected size updates input[name="id"] so it doesn't always add Small
   - Fixes Broadcast product page: label-click doesn't toggle checked UI reliably
*/
(function () {
  function textIncludes(el, needle) {
    return ((el && el.textContent) || "").trim().toLowerCase().includes(needle);
  }

  // Prefer an open quick-add dialog context; fall back to product-info; then document
  function getRoot() {
    return (
      document.querySelector("dialog.product-quick-add[open]") ||
      document.querySelector("product-info") ||
      document
    );
  }

  function findSizeFieldset(root) {
    const legends = Array.from(root.querySelectorAll("legend"));
    const sizeLegend = legends.find((l) => textIncludes(l, "size"));
    return sizeLegend ? sizeLegend.closest("fieldset") : null;
  }

  function getSizeRadios(sizeFieldset) {
    if (!sizeFieldset) return [];
    return Array.from(sizeFieldset.querySelectorAll('input[type="radio"]'));
  }

  function getVariantIdInputs(root) {
    return Array.from(root.querySelectorAll('form[action*="/cart/add"] input[name="id"]'));
  }

  function getProductFormEl(root) {
    return root.querySelector("product-form") || null;
  }

  // ---- Dynamic checkout (Shop Pay / Buy it now) gating ----
  function getDynamicCheckoutWrap(root) {
    return (
      root.querySelector(".shopify-payment-button") ||
      root.querySelector('[data-shopify="payment-button"]') ||
      null
    );
  }

  function setDynamicCheckoutState(root, disabled) {
    const wrap = getDynamicCheckoutWrap(root);
    if (!wrap) return;

    wrap.classList.toggle("is-size-gated", !!disabled);
    wrap.style.pointerEvents = disabled ? "none" : "";
    wrap.style.opacity = disabled ? "0.5" : "";
    wrap.setAttribute("aria-disabled", disabled ? "true" : "false");
  }

  // ---- Add to cart button gating ----
  function setButtonState(root, disabled, labelText) {
    const productFormEl = getProductFormEl(root);

    // Use theme method if available (main product)
    if (productFormEl && typeof productFormEl.toggleSubmitButton === "function") {
      productFormEl.toggleSubmitButton(!!disabled, labelText);
      return;
    }

    // Fallback manual (quick add forms etc.)
    const form = root.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    const btn = form.querySelector('button[name="add"], [data-add-to-cart]');
    if (!btn) return;

    btn.disabled = !!disabled;

    const txt = btn.querySelector("[data-add-to-cart-text]");
    if (txt && labelText) txt.textContent = labelText;
  }

  function clearVariantId(root) {
    const inputs = getVariantIdInputs(root);
    inputs.forEach((inp) => {
      inp.value = "";
      inp.setAttribute("value", "");
      inp.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  function clearSizeUI(sizeFieldset) {
    const radios = getSizeRadios(sizeFieldset);
    radios.forEach((r) => {
      r.checked = false;
      r.removeAttribute("checked");
    });

    // Broadcast often styles via wrapper/label "active" classes
    sizeFieldset.querySelectorAll(".radio__button.is-active").forEach((el) => el.classList.remove("is-active"));
    sizeFieldset.querySelectorAll("label.is-active").forEach((el) => el.classList.remove("is-active"));
    sizeFieldset.querySelectorAll('label[aria-current="true"]').forEach((el) => el.removeAttribute("aria-current"));

    const selectedValueEl = sizeFieldset.querySelector("[data-selected-value]");
    if (selectedValueEl) selectedValueEl.textContent = "";
  }

  function bindSubmitGuard(root) {
    root.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (form.dataset.sizeGateBound === "1") return;
      form.dataset.sizeGateBound = "1";

      form.addEventListener("submit", (e) => {
        const idInput = form.querySelector('input[name="id"]');
        if (!idInput || !idInput.value) {
          e.preventDefault();
          e.stopPropagation();
          setButtonState(root, true, "Select size");
          setDynamicCheckoutState(root, true);
        }
      });
    });
  }

  // Ensure we always act on the REAL radio, even when clicking label/span
  function getRadioFromEventTarget(sizeFieldset, target) {
    if (!sizeFieldset || !target) return null;

    // 1) If input itself
    if (target.matches && target.matches('input[type="radio"]')) return target;

    // 2) If label clicked
    if (target.matches && target.matches("label")) {
      const forId = target.getAttribute("for");
      if (forId) {
        const inp = sizeFieldset.querySelector(`#${CSS.escape(forId)}`);
        if (inp && inp.matches('input[type="radio"]')) return inp;
      }
      // fallback: radio inside same wrapper
      const wrap = target.closest(".radio__button");
      if (wrap) {
        const inp = wrap.querySelector('input[type="radio"]');
        if (inp) return inp;
      }
    }

    // 3) Any nested element inside label/span
    const label = target.closest ? target.closest("label") : null;
    if (label) return getRadioFromEventTarget(sizeFieldset, label);

    const wrap = target.closest ? target.closest(".radio__button") : null;
    if (wrap) {
      const inp = wrap.querySelector('input[type="radio"]');
      if (inp) return inp;
    }

    return null;
  }

  // QUICK ADD FIX: when a radio has data-variant-id, write it into its form's hidden id input
  function setVariantIdFromRadio(root, radio) {
    if (!radio) return false;

    const variantId =
      radio.dataset && radio.dataset.variantId ? String(radio.dataset.variantId).trim() : "";
    if (!variantId) return false;

    let form = null;
    const formId = radio.getAttribute("form");
    if (formId) form = document.getElementById(formId);

    if (!form) form = radio.closest('form[action*="/cart/add"]') || root.querySelector('form[action*="/cart/add"]');
    if (!form) return false;

    const idInput = form.querySelector('input[name="id"]');
    if (!idInput) return false;

    idInput.value = variantId;
    idInput.setAttribute("value", variantId);
    idInput.dispatchEvent(new Event("change", { bubbles: true }));
    return true;
  }

  // Force checked property + checked attribute + Broadcast “active” styling hooks
  function forceRadioChecked(sizeFieldset, radio) {
    if (!sizeFieldset || !radio) return;
    if (!sizeFieldset.contains(radio)) return;

    const name = radio.getAttribute("name");
    if (!name) return;

    const radios = Array.from(
      sizeFieldset.querySelectorAll(`input[type="radio"][name="${CSS.escape(name)}"]`)
    );

    radios.forEach((r) => {
      const isOn = r === radio;

      // property
      r.checked = isOn;

      // attribute (what you explicitly asked for)
      if (isOn) r.setAttribute("checked", "checked");
      else r.removeAttribute("checked");

      // wrapper class (Broadcast commonly styles off wrapper)
      const wrap = r.closest(".radio__button");
      if (wrap) wrap.classList.toggle("is-active", isOn);

      // label class + aria-current (some Broadcast builds use this)
      if (r.id) {
        const lab = sizeFieldset.querySelector(`label[for="${CSS.escape(r.id)}"]`);
        if (lab) {
          lab.classList.toggle("is-active", isOn);
          if (isOn) lab.setAttribute("aria-current", "true");
          else lab.removeAttribute("aria-current");
        }
      }
    });

    // Update legend value display if present
    const selectedValueEl = sizeFieldset.querySelector("[data-selected-value]");
    if (selectedValueEl) selectedValueEl.textContent = radio.value || "";
  }

  function initSizeGateOnce() {
    const root = getRoot();
    const sizeFieldset = findSizeFieldset(root);
    if (!sizeFieldset) return;

    const radios = getSizeRadios(sizeFieldset);
    if (!radios.length) return;

    if (sizeFieldset.dataset.sizeGateInit === "1") return;
    sizeFieldset.dataset.sizeGateInit = "1";

    let userPicked = false;

    function applyGate() {
      if (userPicked) return;

      clearSizeUI(sizeFieldset);
      clearVariantId(root);

      setButtonState(root, true, "Select size");
      setDynamicCheckoutState(root, true);
    }

    function onPick(e) {
      userPicked = true;

      setButtonState(root, true, "Updating…");
      setDynamicCheckoutState(root, true);

      // ✅ normalize to the real radio input (Broadcast label-click fix)
      const clicked = getRadioFromEventTarget(sizeFieldset, e && e.target ? e.target : null);
      if (!clicked) return;

      // ✅ make UI reflect the selection immediately
      forceRadioChecked(sizeFieldset, clicked);

      // ✅ and again after Broadcast finishes its internal handler / possible rerender tick
      setTimeout(() => {
        const freshRoot = getRoot();
        const freshFs = findSizeFieldset(freshRoot);
        if (!freshFs) return;
        const freshClicked =
          freshFs.querySelector(`#${CSS.escape(clicked.id)}`) ||
          Array.from(getSizeRadios(freshFs)).find((r) => r.value === clicked.value && r.getAttribute("name") === clicked.getAttribute("name"));
        if (freshClicked) forceRadioChecked(freshFs, freshClicked);
      }, 0);

      setTimeout(() => {
        const freshRoot = getRoot();
        const freshFs = findSizeFieldset(freshRoot);
        if (!freshFs) return;
        const freshClicked =
          freshFs.querySelector(`#${CSS.escape(clicked.id)}`) ||
          Array.from(getSizeRadios(freshFs)).find((r) => r.value === clicked.value && r.getAttribute("name") === clicked.getAttribute("name"));
        if (freshClicked) forceRadioChecked(freshFs, freshClicked);
      }, 80);

      // ✅ critical: update variant id immediately in the correct form (fixes quick add always adding Small)
      setVariantIdFromRadio(root, clicked);

      const addToCart =
        (window.theme && window.theme.strings && window.theme.strings.addToCart) || "Add to cart";

      const start = Date.now();
      const maxWait = 1200;
      const interval = 50;

      const timer = setInterval(() => {
        const idInputs = getVariantIdInputs(getRoot());
        const hasId = idInputs.some((i) => i.value && i.value !== "");

        if (hasId) {
          clearInterval(timer);
          setButtonState(root, false, addToCart);
          setDynamicCheckoutState(root, false);
          return;
        }

        if (Date.now() - start > maxWait) {
          clearInterval(timer);
          setButtonState(root, true, "Unavailable");
          setDynamicCheckoutState(root, true);
        }
      }, interval);
    }

    // Listen on both radios AND wrapper to catch label clicks reliably
    radios.forEach((r) => {
      r.addEventListener("change", onPick);
      r.addEventListener("click", onPick);
    });

    // Capture label clicks too (Broadcast often triggers label click path)
    sizeFieldset.addEventListener("click", function (e) {
      const t = e && e.target ? e.target : null;
      const maybe = getRadioFromEventTarget(sizeFieldset, t);
      if (!maybe) return;
      // Don't double-run if the input click already fired
      onPick({ target: maybe });
    });

    bindSubmitGuard(root);
    setTimeout(applyGate, 50);
  }

  function resetInitFlagsAndRun() {
    const root = getRoot();
    root.querySelectorAll("fieldset").forEach((fs) => fs.removeAttribute("data-size-gate-init"));
    initSizeGateOnce();
  }

  function boot() {
    initSizeGateOnce();

    document.addEventListener("theme:product-info:loaded", resetInitFlagsAndRun);
    document.addEventListener("shopify:section:load", resetInitFlagsAndRun);

    // When quick-add modal opens, content loads async — run again shortly after clicks
    document.addEventListener("click", (e) => {
      const btn = e.target && e.target.closest ? e.target.closest("[data-quick-add-btn]") : null;
      if (!btn) return;
      setTimeout(resetInitFlagsAndRun, 150);
      setTimeout(resetInitFlagsAndRun, 400);
    });
  }

  document.addEventListener("DOMContentLoaded", boot);
})();